<template>
  <div class="space-y-4">
    <section class="app-section">
      <div class="app-toolbar items-center justify-between">
        <h2 class="m-0 text-xl font-semibold">Настройки системы</h2>
      </div>

      <div v-if="error" class="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        {{ error }}
      </div>
      <div v-if="successMessage" class="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400">
        {{ successMessage }}
      </div>

      <div class="grid gap-4">
        <div class="space-y-2">
          <label for="telegram_bot_token" class="text-sm font-medium">Telegram Bot Token</label>
          <div class="flex flex-col gap-2 sm:flex-row">
            <input
              id="telegram_bot_token"
              v-model="form.telegram_bot_token"
              :type="showToken ? 'text' : 'password'"
              class="input-field flex-1"
              placeholder="123456789:AA..."
              autocomplete="off"
            />
            <button class="button button-outline whitespace-nowrap" type="button" @click="showToken = !showToken">
              {{ showToken ? 'Скрыть' : 'Показать' }}
            </button>
          </div>
        </div>

        <div class="space-y-2">
          <label for="telegram_chat_id" class="text-sm font-medium">Telegram Chat ID</label>
          <input
            id="telegram_chat_id"
            v-model="form.telegram_chat_id"
            type="text"
            class="input-field"
            placeholder="-1001234567890"
            autocomplete="off"
          />
        </div>

        <div v-if="telegramWebhookUrl" class="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Webhook: {{ telegramWebhookUrl }}
        </div>

        <div class="flex justify-end">
          <button class="button" type="button" :disabled="loading || saving" @click="saveSettings">
            {{ saving ? 'Сохранение...' : 'Сохранить настройки' }}
          </button>
        </div>
      </div>
    </section>

    <section class="app-section">
      <h3 class="mb-2 text-base font-semibold">Категории пользователей</h3>
      <p class="mb-3 text-sm text-muted-foreground">
        Здесь настраиваются категории, которые можно выбирать у пользователей и использовать в фильтрах.
      </p>

      <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          v-model="newCategoryName"
          type="text"
          class="input-field flex-1"
          placeholder="Новая категория (например, Video production)"
        />
        <button
          class="button button-outline whitespace-nowrap"
          type="button"
          :disabled="!trimmedNewCategoryName || isCategorySaving"
          @click="handleCreateCategory"
        >
          Добавить категорию
        </button>
      </div>

      <div v-if="categoryError" class="mt-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
        {{ categoryError }}
      </div>

      <div v-if="userCategories.length" class="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
        <span class="font-semibold">Текущие категории:</span>
        <span
          v-for="category in userCategories"
          :key="category.id"
          class="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5"
        >
          <span>{{ category.name }}</span>
          <button
            type="button"
            class="text-destructive hover:text-destructive/80"
            :disabled="isCategoryDeleting"
            @click="handleDeleteCategory(category)"
          >
            ×
          </button>
        </span>
      </div>
      <p v-else class="mt-3 text-xs text-muted-foreground">
        Категории еще не созданы.
      </p>
    </section>
  </div>
</template>

<script>
import {
  fetchSystemSettings,
  updateSystemSettings,
  getAdminUserCategoriesApi,
  createAdminUserCategoryApi,
  deleteAdminUserCategoryApi
} from '../services/api';

export default {
  name: 'SystemSettingsView',
  data() {
    return {
      loading: false,
      saving: false,
      showToken: false,
      error: '',
      successMessage: '',
      telegramWebhookUrl: '',
      form: {
        telegram_bot_token: '',
        telegram_chat_id: '',
      },
      userCategories: [],
      newCategoryName: '',
      isCategorySaving: false,
      isCategoryDeleting: false,
      categoryError: ''
    };
  },
  computed: {
    trimmedNewCategoryName() {
      return (this.newCategoryName || '').trim();
    }
  },
  methods: {
    async loadSettings() {
      this.loading = true;
      this.error = '';
      try {
        const response = await fetchSystemSettings();
        this.form.telegram_bot_token = response.telegram_bot_token || '';
        this.form.telegram_chat_id = response.telegram_chat_id || '';
        this.telegramWebhookUrl = response.telegram_webhook_url || '';
      } catch (error) {
        this.error = error.message || 'Не удалось загрузить системные настройки';
      } finally {
        this.loading = false;
      }
    },
    async saveSettings() {
      this.saving = true;
      this.error = '';
      this.successMessage = '';
      try {
        const response = await updateSystemSettings({
          telegram_bot_token: this.form.telegram_bot_token,
          telegram_chat_id: this.form.telegram_chat_id,
        });
        this.form.telegram_bot_token = response.telegram_bot_token || '';
        this.form.telegram_chat_id = response.telegram_chat_id || '';
        this.telegramWebhookUrl = response.telegram_webhook_url || '';
        this.successMessage = 'Системные настройки сохранены';
      } catch (error) {
        this.error = error.message || 'Не удалось сохранить настройки';
      } finally {
        this.saving = false;
      }
    },
    async loadCategories() {
      this.categoryError = '';
      try {
        this.userCategories = await getAdminUserCategoriesApi();
      } catch (error) {
        console.error('Не удалось загрузить категории пользователей:', error);
        this.userCategories = [];
        this.categoryError = error.message || 'Не удалось загрузить категории пользователей';
      }
    },
    async handleCreateCategory() {
      const name = this.trimmedNewCategoryName;
      if (!name) {
        return;
      }
      this.isCategorySaving = true;
      this.categoryError = '';
      try {
        await createAdminUserCategoryApi(name);
        this.newCategoryName = '';
        await this.loadCategories();
      } catch (error) {
        console.error('Ошибка создания категории пользователя:', error);
        this.categoryError = error?.message || 'Ошибка при создании категории';
      } finally {
        this.isCategorySaving = false;
      }
    },
    async handleDeleteCategory(category) {
      if (!category?.id) {
        return;
      }
      if (!confirm(`Удалить категорию "${category.name}"? У пользователей она будет сброшена.`)) {
        return;
      }
      this.isCategoryDeleting = true;
      this.categoryError = '';
      try {
        await deleteAdminUserCategoryApi(category.id);
        await this.loadCategories();
      } catch (error) {
        console.error('Ошибка удаления категории пользователя:', error);
        this.categoryError = error?.message || 'Ошибка при удалении категории';
      } finally {
        this.isCategoryDeleting = false;
      }
    }
  },
  mounted() {
    this.loadSettings();
    this.loadCategories();
  },
};
</script>
